import { useMemo } from "react";

import {
  Select,
  type SelectOption,
  type SelectProps,
} from "@/components/ui/select";

export interface PartySelectEntry {
  value: string;
  companyName?: string | null;
  contactName?: string | null;
  email?: string | null;
  disabled?: boolean;
}

interface PartySelectProps extends Omit<SelectProps, "options"> {
  parties: PartySelectEntry[];
  labelBy?: "company" | "contact";
}

function partySelectOption(
  party: PartySelectEntry,
  labelBy: "company" | "contact",
): SelectOption {
  const company = party.companyName?.trim() ?? "";
  const contact = party.contactName?.trim() ?? "";
  const email = party.email?.trim() ?? "";
  const preferred = labelBy === "company" ? company : contact;
  const alternate = labelBy === "company" ? contact : company;

  return {
    value: party.value,
    label: preferred || alternate || email || "—",
    description: alternate || undefined,
    // Only search visible row text. Hidden email matches make unrelated
    // companies appear in results with no explanation.
    searchText: [company, contact].filter(Boolean).join(" "),
    disabled: party.disabled,
  };
}

/**
 * Shared company/contact picker for question-configured parties such as OMT
 * and AMS. All portals use the same row formatting and searchable fields.
 */
export function PartySelect({
  parties,
  labelBy = "company",
  ...selectProps
}: PartySelectProps) {
  const options = useMemo(
    () =>
      parties
        .map((party) => partySelectOption(party, labelBy))
        .sort((left, right) =>
          left.label.localeCompare(right.label, undefined, {
            numeric: true,
            sensitivity: "base",
          }),
        ),
    [parties, labelBy],
  );

  return <Select {...selectProps} options={options} />;
}
